/**
 * Layer-3 (May 2026) — Conversation checkpoint extractor.
 *
 * Why this exists:
 *   When a long chat dies (413, message-cap, user click), the previous UX
 *   was a hard reset. The next chat lost intent, lost status, and the user
 *   had to re-explain. For a CFO doing a multi-day board pack that was
 *   not just annoying — it was a token-economics failure (re-priming the
 *   system prompt + tool definitions costs ~30K input tokens before any
 *   useful work happens).
 *
 *   This module produces a structured handoff note ("checkpoint") from the
 *   dying conversation's history. The new conversation is seeded with a
 *   single, focused user message built from the checkpoint, plus a request
 *   to re-snapshot the workbook. The workbook itself is the source of
 *   truth for what's been built; chat history is only the source of truth
 *   for INTENT and OPEN WORK. The checkpoint distils both.
 *
 * Determinism: this is a pure function over history + ingest. No LLM call,
 * no network. Cheap, predictable, and reversible — if a later release
 * wants LLM-summarised checkpoints, this becomes the fallback.
 *
 * Multi-day, multi-user note: the ingest sessionId expires server-side
 * (in-memory snapshotStore). The checkpoint records the workbook
 * fingerprint (filename + sheet count + cell count) so the resumed
 * conversation can prompt the user to re-ingest if they want the
 * snapshot mode back. The decision to re-ingest belongs to the human.
 */

import { Message, ContentBlock, Checkpoint } from '../types';
import { IngestSummary } from '../types/ingest';

const MAX_INTENT_CHARS = 600;
const MAX_STATUS_CHARS = 800;
const MAX_ERROR_CHARS = 400;

function firstUserText(history: Message[]): string {
  for (const m of history) {
    if (m.role !== 'user') continue;
    for (const b of m.content) {
      if (b.type === 'text' && b.text.trim()) {
        const t = b.text.trim();
        return t.length > MAX_INTENT_CHARS ? t.slice(0, MAX_INTENT_CHARS - 1) + '…' : t;
      }
    }
  }
  return '';
}

function lastAssistantText(history: Message[]): string {
  for (let i = history.length - 1; i >= 0; i--) {
    const m = history[i];
    if (m.role !== 'assistant') continue;
    // Walk blocks in reverse: the last text block in the last assistant
    // message is the most recent narration. If the assistant's last act
    // was a tool_use with no trailing text, walk back further.
    for (let j = m.content.length - 1; j >= 0; j--) {
      const b = m.content[j];
      if (b.type === 'text' && b.text.trim()) {
        const t = b.text.trim();
        return t.length > MAX_STATUS_CHARS ? t.slice(-MAX_STATUS_CHARS + 1) + '…' : t;
      }
    }
  }
  return '';
}

function toolCallHistogram(history: Message[]): { name: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const m of history) {
    if (m.role !== 'assistant') continue;
    for (const b of m.content) {
      if (b.type === 'tool_use') {
        counts.set(b.name, (counts.get(b.name) ?? 0) + 1);
      }
    }
  }
  return [...counts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
}

function lastError(history: Message[]): string | null {
  for (let i = history.length - 1; i >= 0; i--) {
    const m = history[i];
    if (m.role !== 'user') continue;
    for (let j = m.content.length - 1; j >= 0; j--) {
      const b = m.content[j];
      if (b.type === 'tool_result' && b.is_error) {
        const t = (b.content || '').trim();
        if (!t) continue;
        return t.length > MAX_ERROR_CHARS ? t.slice(0, MAX_ERROR_CHARS - 1) + '…' : t;
      }
    }
  }
  return null;
}

/**
 * Best-effort extraction of recently-touched sheets, by scanning compacted
 * tool_use inputs for sheet/sheet_name/target_sheet keys. Robust to the
 * Layer-2 _compacted_summary shape — when present, mines the summary text
 * for bracketed sheet names.
 */
function recentSheetsTouched(history: Message[]): string[] {
  const sheets = new Set<string>();
  // Walk only the LATEST 12 messages — older context is stale by definition
  // of "where are we now".
  const tail = history.slice(-12);
  for (const m of tail) {
    if (m.role !== 'assistant') continue;
    for (const b of m.content) {
      if (b.type !== 'tool_use') continue;
      const input = b.input;
      if (input && typeof input === 'object' && !Array.isArray(input)) {
        const obj = input as Record<string, unknown>;
        for (const k of ['sheet', 'sheet_name', 'target_sheet']) {
          const v = obj[k];
          if (typeof v === 'string' && v.trim()) sheets.add(v.trim());
        }
        // Layer-2 summary form: scan the summary text for bracketed sheet names.
        const summary = obj._compacted_summary;
        if (typeof summary === 'string') {
          const m2 = summary.match(/on \[([^\]]+)\]/);
          if (m2) {
            for (const s of m2[1].split(',').map(x => x.trim()).filter(Boolean)) {
              sheets.add(s);
            }
          }
        }
        // The plan op array (uncompacted form): mine top-level sheet fields.
        const plan = obj.plan;
        if (Array.isArray(plan)) {
          for (const op of plan) {
            if (op && typeof op === 'object') {
              const oo = op as Record<string, unknown>;
              for (const k of ['sheet', 'sheet_name', 'target_sheet']) {
                const v = oo[k];
                if (typeof v === 'string' && v.trim()) sheets.add(v.trim());
              }
            }
          }
        }
      }
    }
  }
  return [...sheets].slice(0, 8);
}

export function deriveCheckpoint(
  history: Message[],
  ingest: IngestSummary | null | undefined,
  sourceConversationTitle: string,
): Checkpoint {
  return {
    sourceConversationTitle,
    createdAt: new Date().toISOString(),
    intent: firstUserText(history),
    lastStatus: lastAssistantText(history),
    toolHistogram: toolCallHistogram(history),
    sheetsTouched: recentSheetsTouched(history),
    lastError: lastError(history),
    workbookFingerprint: ingest
      ? {
          fileName: ingest.fileName,
          sheetCount: ingest.sheetCount,
          totalCells: ingest.totalCells,
          summary: ingest.summary,
        }
      : null,
    messageCount: history.length,
  };
}

/**
 * Render a checkpoint as a human-readable user-role message that primes
 * the new conversation. This is what the LLM sees on first turn — it
 * replaces the entire dying chat history with a single focused note.
 *
 * Format choice: Markdown-flavoured plain text. Numbered ask at the end
 * forces the agent into a structured first response — "what's done /
 * what's open / what's next" — instead of free-roaming.
 */
export function renderCheckpointAsPrimer(cp: Checkpoint): string {
  const parts: string[] = [];
  parts.push(`**Continuing from a prior session** ("${cp.sourceConversationTitle}", ${cp.messageCount} messages).`);
  parts.push('');
  if (cp.intent) {
    parts.push(`**Original goal:**\n${cp.intent}`);
    parts.push('');
  }
  if (cp.workbookFingerprint) {
    const wb = cp.workbookFingerprint;
    parts.push(
      `**Workbook in scope:** ${wb.fileName} · ${wb.sheetCount} sheet(s) · ${wb.totalCells.toLocaleString()} cells.`,
    );
    parts.push(
      `_Note: the prior server-side snapshot has expired. If you need ingested-mode tools (query_snapshot, apply_workbook_plan), I will re-ingest the workbook when you ask._`,
    );
    parts.push('');
  }
  if (cp.lastStatus) {
    parts.push(`**Status when the last chat ended:**\n${cp.lastStatus}`);
    parts.push('');
  }
  if (cp.sheetsTouched.length > 0) {
    parts.push(`**Sheets touched recently:** ${cp.sheetsTouched.map(s => `\`${s}\``).join(', ')}`);
    parts.push('');
  }
  if (cp.toolHistogram.length > 0) {
    const top = cp.toolHistogram.slice(0, 6).map(t => `${t.count}× ${t.name}`).join(', ');
    parts.push(`**Tools used so far:** ${top}${cp.toolHistogram.length > 6 ? ', …' : ''}.`);
    parts.push('');
  }
  if (cp.lastError) {
    parts.push(`**Last error encountered:**\n\`${cp.lastError}\``);
    parts.push('');
  }
  parts.push(`**Please respond with:**`);
  parts.push(`1. **What's already done** in the workbook (read the relevant sheet(s) to confirm — don't trust the prior chat alone).`);
  parts.push(`2. **What's still open** based on the original goal.`);
  parts.push(`3. **Your recommended next step** — propose, don't execute. I'll confirm before you build.`);
  return parts.join('\n');
}

/**
 * Convenience wrapper used by App.tsx — returns the primer content blocks
 * ready to splice into a new conversation's history.
 */
export function buildResumeMessage(
  history: Message[],
  ingest: IngestSummary | null | undefined,
  sourceConversationTitle: string,
): { history: Message[]; checkpoint: Checkpoint } {
  const checkpoint = deriveCheckpoint(history, ingest, sourceConversationTitle);
  const primer = renderCheckpointAsPrimer(checkpoint);
  const userMsg: Message = {
    role: 'user',
    content: [{ type: 'text', text: primer } as ContentBlock],
  };
  return { history: [userMsg], checkpoint };
}
